/**
 * Orchestration Service
 * AIDLC Administrative Backend
 *
 * API calls for Budget Proposals, Milestones, Token Roadmap,
 * Agent Scheduler, Assessment Reports, and Spawn Plans.
 */

import apiClient from './api';

// ── Types ────────────────────────────────────────────────────────────

/** Budget breakdown within a proposal */
export interface ProposalBudget {
  totalBudget: number;
  orchestrationOverhead: number;
  dodOverhead: number;
  reworkBuffer: number;
  netAvailable: number;
}

/** A story included in a proposal */
export interface ProposalStory {
  backlogItemId: string;
  title: string;
  sizeCategory: 'S' | 'M' | 'L' | 'XL';
  tokenEstimate: number;
  priority: 'must' | 'should' | 'could' | 'wont';
  requiredAgents: string[];
}

/** Proposal status */
export type ProposalStatus = 'draft' | 'pending_review' | 'approved' | 'declined';

/** Full proposal object */
export interface Proposal {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: ProposalStatus;
  budget: ProposalBudget;
  stories: ProposalStory[];
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  declineReason?: string;
  milestoneId?: string;
}

/** Gate status within a milestone */
export type GateStatus = 'pending' | 'in_progress' | 'passed' | 'failed' | 'blocked';

/** A gate in the milestone pipeline */
export interface MilestoneGate {
  id: string;
  name: string;
  status: GateStatus;
  notes?: string;
  checklist?: Array<{ item: string; done: boolean }> | string[];
  passedAt?: string;
  failedAt?: string;
}

/** Raw gate status object as returned by the backend */
interface BackendGateStatus {
  status: GateStatus;
  passedAt?: string;
  notes?: string;
  checklist?: Array<{ item: string; done: boolean }>;
}

/** Raw milestone record as returned by the backend */
interface BackendMilestoneRecord {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: 'planning' | 'active' | 'completed' | 'cancelled';
  proposalId?: string;
  gates: {
    g1_backlog_ready: BackendGateStatus;
    g2_budget_approved: BackendGateStatus;
    g3_agents_spawned: BackendGateStatus;
    g4_code_complete: BackendGateStatus;
    g5_dod_passed: BackendGateStatus;
    g6_shipped: BackendGateStatus;
  };
  backlogItemIds: string[];
  totalTokenBudget: number;
  tokensSpent: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

/** Gate key to human-readable name mapping */
const GATE_NAMES: Record<string, string> = {
  g1_backlog_ready: 'Backlog Ready',
  g2_budget_approved: 'Budget Approved',
  g3_agents_spawned: 'Agents Spawned',
  g4_code_complete: 'Code Complete',
  g5_dod_passed: 'DoD Passed',
  g6_shipped: 'Shipped',
};

/** Transform backend gate object into frontend gate array */
function transformGates(
  gates: BackendMilestoneRecord['gates'],
): MilestoneGate[] {
  return Object.entries(gates).map(([key, gate]) => ({
    id: key,
    name: GATE_NAMES[key] || key,
    status: gate.status,
    notes: gate.notes,
    checklist: gate.checklist,
    passedAt: gate.passedAt,
  }));
}

/** Transform a backend milestone record to the frontend Milestone shape */
function transformMilestone(raw: BackendMilestoneRecord): Milestone {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description || undefined,
    status: raw.status,
    proposalId: raw.proposalId,
    projectId: raw.projectId,
    tokenBudget: raw.totalTokenBudget,
    tokensSpent: raw.tokensSpent,
    gates: transformGates(raw.gates),
    backlogItems: raw.backlogItemIds,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

/** Full milestone object */
export interface Milestone {
  id: string;
  title: string;
  description?: string;
  status: 'planning' | 'active' | 'completed' | 'cancelled';
  proposalId?: string;
  projectId?: string;
  tokenBudget: number;
  tokensSpent: number;
  gates: MilestoneGate[];
  backlogItems?: string[];
  createdAt: string;
  updatedAt: string;
}

/** Roadmap item (computed from backlog + budget) */
export interface RoadmapItem {
  id: string;
  title: string;
  type: string;
  priority: string;
  moscow: string | null;
  status: string;
  assignedAgent: string | null;
  project: string;
  sizeCategory: string | null;
  tokenEstimate: number;
  estimatedCost: number;
  milestoneId: string | null;
  sprintId: string | null;
}

/** Roadmap summary statistics from backend */
export interface RoadmapSummary {
  totalItems: number;
  estimatedItems: number;
  unestimatedItems: number;
  totalTokenEstimate: number;
  totalCostEstimate: number;
  byPriority: Record<string, { count: number; tokens: number; cost: number }>;
  byProject: Record<string, { count: number; tokens: number; cost: number }>;
}

/** Roadmap milestone summary */
export interface RoadmapMilestone {
  id: string;
  title: string;
  status: string;
  totalBudget: number;
  spent: number;
  itemCount: number;
}

/** Roadmap budget health */
export interface RoadmapBudgetHealth {
  monthlyBudget: number;
  spent: number;
  remaining: number;
  projectedMonthlySpend: number;
}

/** Full roadmap response matching backend GET /api/roadmap */
export interface RoadmapData {
  items: RoadmapItem[];
  summary: RoadmapSummary;
  milestones: RoadmapMilestone[];
  budgetHealth: RoadmapBudgetHealth;
}

/** Scenario response matching backend POST /api/roadmap/scenario */
export interface RoadmapScenarioData {
  budgetTokens: number;
  included: {
    items: RoadmapItem[];
    count: number;
    totalTokens: number;
    totalCost: number;
  };
  excluded: {
    items: RoadmapItem[];
    count: number;
    totalTokens: number;
    totalCost: number;
  };
  remainingBudget: number;
}

// ── Proposals API ────────────────────────────────────────────────────

const PROPOSALS_PATH = '/proposals';

/** List all proposals */
export async function getProposals(): Promise<Proposal[]> {
  const response = await apiClient.get<Proposal[]>(PROPOSALS_PATH);
  return response.data;
}

/** Get a single proposal by ID */
export async function getProposal(id: string): Promise<Proposal> {
  const response = await apiClient.get<Proposal>(`${PROPOSALS_PATH}/${id}`);
  return response.data;
}

/** Create a new proposal */
export async function createProposal(data: {
  projectId: string;
  title: string;
  description: string;
  budget: ProposalBudget;
  stories: ProposalStory[];
}): Promise<Proposal> {
  const response = await apiClient.post<Proposal>(PROPOSALS_PATH, data);
  return response.data;
}

/** Submit a draft proposal for review */
export async function submitProposal(id: string): Promise<Proposal> {
  const response = await apiClient.post<Proposal>(`${PROPOSALS_PATH}/${id}/submit`);
  return response.data;
}

/** Approve a proposal */
export async function approveProposal(id: string, reviewedBy: string): Promise<Proposal> {
  const response = await apiClient.post<Proposal>(`${PROPOSALS_PATH}/${id}/approve`, { reviewedBy });
  return response.data;
}

/** Decline a proposal */
export async function declineProposal(id: string, reviewedBy: string, reason: string): Promise<Proposal> {
  const response = await apiClient.post<Proposal>(`${PROPOSALS_PATH}/${id}/decline`, { reviewedBy, reason });
  return response.data;
}

// ── Milestones API ───────────────────────────────────────────────────

const MILESTONES_PATH = '/milestones';

/** List all milestones */
export async function getMilestones(): Promise<Milestone[]> {
  const response = await apiClient.get<BackendMilestoneRecord[]>(MILESTONES_PATH);
  return response.data.map(transformMilestone);
}

/** Get a single milestone by ID */
export async function getMilestone(id: string): Promise<Milestone> {
  const response = await apiClient.get<BackendMilestoneRecord>(`${MILESTONES_PATH}/${id}`);
  return transformMilestone(response.data);
}

/** Create a new milestone */
export async function createMilestone(data: {
  title: string;
  description?: string;
  proposalId?: string;
  projectId: string;
  totalTokenBudget: number;
}): Promise<Milestone> {
  const response = await apiClient.post<BackendMilestoneRecord>(MILESTONES_PATH, data);
  return transformMilestone(response.data);
}

/** Pass a gate on a milestone */
export async function passGate(milestoneId: string, gateId: string, notes?: string): Promise<Milestone> {
  const response = await apiClient.post<BackendMilestoneRecord>(
    `${MILESTONES_PATH}/${milestoneId}/gates/${gateId}/pass`,
    { notes },
  );
  return transformMilestone(response.data);
}

/** Fail a gate on a milestone */
export async function failGate(milestoneId: string, gateId: string, notes?: string): Promise<Milestone> {
  const response = await apiClient.post<BackendMilestoneRecord>(
    `${MILESTONES_PATH}/${milestoneId}/gates/${gateId}/fail`,
    { notes },
  );
  return transformMilestone(response.data);
}

// ── Roadmap API ──────────────────────────────────────────────────────

const ROADMAP_PATH = '/roadmap';

/** Get the full roadmap view */
export async function getRoadmap(): Promise<RoadmapData> {
  const response = await apiClient.get<RoadmapData>(ROADMAP_PATH);
  return response.data;
}

/** Run a what-if scenario with a different budget limit */
export async function getRoadmapScenario(budgetTokens: number): Promise<RoadmapScenarioData> {
  const response = await apiClient.post<RoadmapScenarioData>(`${ROADMAP_PATH}/scenario`, { budgetTokens });
  return response.data;
}

// ── Scheduler Types ─────────────────────────────────────────────────

/** Trigger names the scheduler recognises (camelCase, matching backend) */
export type SchedulerTrigger =
  | 'ceoDirective'
  | 'newBacklogItems'
  | 'agentCompletion'
  | 'blockerDetected'
  | 'budgetReplenishment'
  | 'milestoneGateReached';

/** Budget-aware dormancy settings (matching backend field names) */
export interface DormancySettings {
  budgetThresholdPercent: number;
  reducedIntervalHours: number;
  fullDormancyAtZero: boolean;
}

/** Full scheduler configuration (matching backend) */
export interface SchedulerConfig {
  enabled: boolean;
  intervalHours: number;
  triggers: Record<SchedulerTrigger, boolean>;
  dormancy: DormancySettings;
  lastWakeUp?: string;
  nextWakeUp?: string;
}

/** A single wake-up history entry */
export interface WakeUpHistoryEntry {
  id: string;
  timestamp: string;
  triggeredBy: string;
  assessmentId?: string;
  notes: string;
}

/** Next scheduled wake-up response */
export interface NextWakeUp {
  enabled: boolean;
  nextWakeUp: string | null;
  intervalHours: number;
  lastWakeUp: string | null;
  engine?: { isRunning: boolean; hasTimer: boolean; hasIoRef: boolean };
}

// ── Assessment Types ────────────────────────────────────────────────

/** Assessment recommendation */
export type AssessmentRecommendation =
  | 'ready_to_spawn'
  | 'needs_estimation'
  | 'needs_budget'
  | 'needs_prioritization'
  | 'dormant';

/** Assessment trigger (matching backend valid triggers) */
export type AssessmentTrigger =
  | 'scheduled'
  | 'ceo_directive'
  | 'new_backlog'
  | 'agent_completion'
  | 'blocker_detected'
  | 'budget_replenishment'
  | 'milestone_gate';

/** Priority breakdown in backlog snapshot */
export interface PriorityBreakdown {
  must: number;
  should: number;
  could: number;
  wont: number;
}

/** Backlog snapshot within an assessment (matching backend field names) */
export interface BacklogSnapshot {
  totalItems: number;
  unestimated: number;
  estimated: number;
  byPriority: PriorityBreakdown;
}

/** Budget snapshot within an assessment (matching backend field names) */
export interface BudgetSnapshot {
  totalBudget: number;
  spent: number;
  remaining: number;
  burnRate: number;
}

/** Full assessment report (matching backend AssessmentReport) */
export interface AssessmentReport {
  id: string;
  triggeredBy: AssessmentTrigger;
  recommendation: AssessmentRecommendation;
  backlogSnapshot: BacklogSnapshot;
  budgetSnapshot: BudgetSnapshot;
  milestoneCandidates: string[];
  notes: string;
  agentId: string;
  createdAt: string;
}

// ── Spawn Plan Types ────────────────────────────────────────────────

/** Spawn plan status */
export type SpawnPlanStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'executing'
  | 'completed'
  | 'cancelled';

/** Agent status within a spawn plan phase */
export type SpawnAgentStatus = 'pending' | 'spawned' | 'completed' | 'failed';

/** An agent assignment within a phase (matching backend SpawnAgent) */
export interface SpawnPlanAgent {
  agentId: string;
  task: string;
  backlogItemIds: string[];
  estimatedTokens: number;
  actualTokens?: number;
  status: SpawnAgentStatus;
}

/** A phase within a spawn plan (matching backend SpawnPhase) */
export interface SpawnPlanPhase {
  phaseNumber: number;
  parallelizable: boolean;
  agents: SpawnPlanAgent[];
  estimatedTokens: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

/** Full spawn plan object (matching backend SpawnPlan) */
export interface SpawnPlan {
  id: string;
  milestoneId: string;
  status: SpawnPlanStatus;
  phases: SpawnPlanPhase[];
  totalEstimatedTokens: number;
  bufferPercent: number;
  totalWithBuffer: number;
  createdAt: string;
  updatedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  completedAt?: string;
}

// ── Scheduler API ───────────────────────────────────────────────────

const SCHEDULER_PATH = '/scheduler';

/** Get current scheduler configuration */
export async function getSchedulerConfig(): Promise<SchedulerConfig> {
  const response = await apiClient.get<SchedulerConfig>(SCHEDULER_PATH);
  return response.data;
}

/** Update scheduler configuration */
export async function updateSchedulerConfig(config: Partial<SchedulerConfig>): Promise<SchedulerConfig> {
  const response = await apiClient.put<SchedulerConfig>(SCHEDULER_PATH, config);
  return response.data;
}

/** Manually wake Sam now — returns the wake event and assessment from the real scheduler engine */
export async function triggerWake(): Promise<{ wakeEvent: WakeUpHistoryEntry; assessment: AssessmentReport }> {
  const response = await apiClient.post<{ wakeEvent: WakeUpHistoryEntry; assessment: AssessmentReport }>(`${SCHEDULER_PATH}/wake`);
  return response.data;
}

/** Get wake-up history */
export async function getWakeUpHistory(): Promise<WakeUpHistoryEntry[]> {
  const response = await apiClient.get<WakeUpHistoryEntry[]>(`${SCHEDULER_PATH}/history`);
  return response.data;
}

/** Get next scheduled wake-up time */
export async function getNextWakeUp(): Promise<NextWakeUp> {
  const response = await apiClient.get<NextWakeUp>(`${SCHEDULER_PATH}/next`);
  return response.data;
}

// ── Assessments API ─────────────────────────────────────────────────

const ASSESSMENTS_PATH = '/assessments';

/** List all assessment reports */
export async function getAssessments(): Promise<AssessmentReport[]> {
  const response = await apiClient.get<AssessmentReport[]>(ASSESSMENTS_PATH);
  return response.data;
}

/** Create a new assessment report */
export async function createAssessment(data: {
  triggeredBy: AssessmentTrigger;
  backlogSnapshot: BacklogSnapshot;
  budgetSnapshot: BudgetSnapshot;
  milestoneCandidates?: string[];
  recommendation: AssessmentRecommendation;
  notes?: string;
  agentId?: string;
}): Promise<AssessmentReport> {
  const response = await apiClient.post<AssessmentReport>(ASSESSMENTS_PATH, data);
  return response.data;
}

/** Get the latest assessment report */
export async function getLatestAssessment(): Promise<AssessmentReport> {
  const response = await apiClient.get<AssessmentReport>(`${ASSESSMENTS_PATH}/latest`);
  return response.data;
}

/** Get a single assessment report by ID */
export async function getAssessment(id: string): Promise<AssessmentReport> {
  const response = await apiClient.get<AssessmentReport>(`${ASSESSMENTS_PATH}/${id}`);
  return response.data;
}

// ── Spawn Plans API ─────────────────────────────────────────────────

const SPAWN_PLANS_PATH = '/spawn-plans';

/** List all spawn plans */
export async function getSpawnPlans(): Promise<SpawnPlan[]> {
  const response = await apiClient.get<SpawnPlan[]>(SPAWN_PLANS_PATH);
  return response.data;
}

/** Create a new spawn plan */
export async function createSpawnPlan(data: {
  milestoneId: string;
  phases: SpawnPlanPhase[];
  totalEstimatedTokens: number;
  bufferPercent?: number;
}): Promise<SpawnPlan> {
  const response = await apiClient.post<SpawnPlan>(SPAWN_PLANS_PATH, data);
  return response.data;
}

/** Get a single spawn plan by ID */
export async function getSpawnPlan(id: string): Promise<SpawnPlan> {
  const response = await apiClient.get<SpawnPlan>(`${SPAWN_PLANS_PATH}/${id}`);
  return response.data;
}

/** Approve a spawn plan */
export async function approveSpawnPlan(id: string): Promise<SpawnPlan> {
  const response = await apiClient.post<SpawnPlan>(`${SPAWN_PLANS_PATH}/${id}/approve`);
  return response.data;
}

/** Execute a spawn plan */
export async function executeSpawnPlan(id: string): Promise<SpawnPlan> {
  const response = await apiClient.post<SpawnPlan>(`${SPAWN_PLANS_PATH}/${id}/execute`);
  return response.data;
}

/** Update an agent's status within a spawn plan phase */
export async function updateSpawnAgent(
  planId: string,
  phase: number,
  agentId: string,
  data: { status: SpawnAgentStatus; actualTokens?: number },
): Promise<SpawnPlan> {
  const response = await apiClient.put<SpawnPlan>(
    `${SPAWN_PLANS_PATH}/${planId}/phases/${phase}/agents/${agentId}`,
    data,
  );
  return response.data;
}

// ── Burn Monitor Types ──────────────────────────────────────────────

/** Budget health status */
export type BurnHealthStatus = 'healthy' | 'warning' | 'critical';

/** Budget health summary from /api/burn-monitor/health (matching backend) */
export interface BurnHealth {
  activeMilestones: number;
  totalBudget: number;
  totalSpent: number;
  totalRemaining: number;
  burnRate: number;
  unacknowledgedAlerts: number;
  status: BurnHealthStatus;
}

/** Burn trend data point (matching backend getTokenTrend) */
export interface BurnTrendPoint {
  date: string;
  spend: number;
  budgetThreshold: number;
}

/** Alert severity levels */
export type AlertSeverity = 'info' | 'warning' | 'critical';

/** Alert type categories */
export type AlertType =
  | 'over_burn'
  | 'agent_blocked'
  | 'budget_low'
  | 'budget_exhausted'
  | 'burn_rate_high';

/** A budget alert (matching backend BurnAlert) */
export interface BurnAlert {
  id: string;
  milestoneId: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  metrics: Record<string, number>;
  acknowledged: boolean;
  acknowledgedAt?: string;
  createdAt: string;
}

/** Intervention type (matching backend valid types) */
export type InterventionType =
  | 'pause_agents'
  | 'reduce_scope'
  | 'add_budget'
  | 'reassign'
  | 'cancel';

/** A budget intervention record (matching backend BurnIntervention) */
export interface BurnIntervention {
  id: string;
  milestoneId: string;
  type: InterventionType;
  description: string;
  initiatedBy: string;
  createdAt: string;
}

// ── Burn Monitor API ────────────────────────────────────────────────

const BURN_MONITOR_PATH = '/burn-monitor';

/** Get budget health summary */
export async function getBurnHealth(): Promise<BurnHealth> {
  const response = await apiClient.get<BurnHealth>(`${BURN_MONITOR_PATH}/health`);
  return response.data;
}

/** Get burn trend data */
export async function getBurnTrend(days?: number): Promise<{ trend: BurnTrendPoint[] }> {
  const response = await apiClient.get<{ trend: BurnTrendPoint[] }>(`${BURN_MONITOR_PATH}/trend`, {
    params: days ? { days } : undefined,
  });
  return response.data;
}

/** Get burn alerts with optional filters */
export async function getBurnAlerts(params?: {
  milestoneId?: string;
  unacknowledged?: boolean;
}): Promise<BurnAlert[]> {
  const response = await apiClient.get<BurnAlert[]>(`${BURN_MONITOR_PATH}/alerts`, { params });
  return response.data;
}

/** Acknowledge a burn alert */
export async function acknowledgeBurnAlert(id: string): Promise<BurnAlert> {
  const response = await apiClient.post<BurnAlert>(
    `${BURN_MONITOR_PATH}/alerts/${id}/acknowledge`,
  );
  return response.data;
}

/** Create a budget intervention */
export async function createIntervention(data: {
  milestoneId: string;
  type: InterventionType;
  description: string;
  initiatedBy?: string;
}): Promise<BurnIntervention> {
  const response = await apiClient.post<BurnIntervention>(
    `${BURN_MONITOR_PATH}/interventions`,
    data,
  );
  return response.data;
}

/** List budget interventions */
export async function getInterventions(): Promise<BurnIntervention[]> {
  const response = await apiClient.get<BurnIntervention[]>(
    `${BURN_MONITOR_PATH}/interventions`,
  );
  return response.data;
}

// ── Escalation Types ────────────────────────────────────────────────

/** Escalation severity */
export type EscalationSeverity = 'low' | 'medium' | 'high' | 'critical';

/** Escalation type categories (matching backend valid types) */
export type EscalationType =
  | 'dev_qa_disagreement'
  | 'scope_creep'
  | 'velocity_drop'
  | 'budget_insufficient'
  | 'priority_conflict'
  | 'quality_gate_failure'
  | 'agent_blocked';

/** Escalation status */
export type EscalationStatus = 'open' | 'in_review' | 'resolved' | 'dismissed';

/** An option presented in an escalation */
export interface EscalationOption {
  label: string;
  description: string;
  tokenCost: number;
  impact: string;
  recommended: boolean;
}

/** Escalation resolution (nested object, matching backend) */
export interface EscalationResolution {
  chosenOptionIndex: number;
  notes: string;
  resolvedBy: string;
  resolvedAt: string;
}

/** Full escalation object (matching backend Escalation) */
export interface Escalation {
  id: string;
  milestoneId?: string;
  type: EscalationType;
  severity: EscalationSeverity;
  status: EscalationStatus;
  situation: string;
  raisedBy: string;
  options: EscalationOption[];
  resolution?: EscalationResolution;
  createdAt: string;
}

// ── Escalations API ─────────────────────────────────────────────────

const ESCALATIONS_PATH = '/escalations';

/** List escalations with optional filters */
export async function getEscalations(params?: {
  status?: EscalationStatus;
  severity?: EscalationSeverity;
}): Promise<Escalation[]> {
  const response = await apiClient.get<Escalation[]>(ESCALATIONS_PATH, { params });
  return response.data;
}

/** Get a single escalation by ID */
export async function getEscalation(id: string): Promise<Escalation> {
  const response = await apiClient.get<Escalation>(`${ESCALATIONS_PATH}/${id}`);
  return response.data;
}

/** Create a new escalation */
export async function createEscalation(data: {
  severity: EscalationSeverity;
  type: EscalationType;
  situation: string;
  raisedBy: string;
  options: EscalationOption[];
}): Promise<Escalation> {
  const response = await apiClient.post<Escalation>(ESCALATIONS_PATH, data);
  return response.data;
}

/** Resolve an escalation by choosing an option */
export async function resolveEscalation(
  id: string,
  data: { chosenOptionIndex: number; notes: string; resolvedBy: string },
): Promise<Escalation> {
  const response = await apiClient.post<Escalation>(
    `${ESCALATIONS_PATH}/${id}/resolve`,
    data,
  );
  return response.data;
}

// ── Backlog Types (DoD Pipeline) ────────────────────────────────────

/** DoD review status for backlog items */
export type DodStatus =
  | 'code_complete'
  | 'bill_review'
  | 'asha_qa'
  | 'done'
  | 'backlog'
  | 'in_progress';

/** Review result on a backlog item */
export interface DodReview {
  reviewer: string;
  rating?: number;
  status?: 'pass' | 'needs_fixes';
  comment?: string;
  reviewedAt: string;
}

/** Backlog item with DoD fields */
export interface BacklogItem {
  id: string;
  title: string;
  description?: string;
  status: string;
  dodStatus?: DodStatus;
  assignedAgent?: string;
  priority: 'must' | 'should' | 'could' | 'wont';
  sizeCategory: 'S' | 'M' | 'L' | 'XL';
  tokenEstimate?: number;
  reviews?: DodReview[];
  createdAt: string;
  updatedAt: string;
}

// ── Backlog API (DoD Pipeline) ──────────────────────────────────────

const BACKLOG_PATH = '/backlog';

/** List backlog items with optional filters */
export async function getBacklogItems(params?: {
  status?: string;
}): Promise<BacklogItem[]> {
  const response = await apiClient.get<BacklogItem[]>(BACKLOG_PATH, { params });
  return response.data;
}

// ── Review Report Types ─────────────────────────────────────────────

/** Finding severity levels */
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low';

/** Finding status */
export type FindingStatus = 'open' | 'fixed' | 'wont_fix' | 'deferred';

/** A single finding within a review report */
export interface ReviewFinding {
  id: string;
  severity: FindingSeverity;
  title: string;
  description: string;
  file: string | null;
  line: number | null;
  status: FindingStatus;
  fixedInCommit: string | null;
}

/** Review report type */
export type ReviewReportType = 'code_review' | 'qa_test';

/** QA test status */
export type QAStatus = 'pass' | 'fail' | 'needs_fixes';

/** Full review report object (matching backend ReviewReport) */
export interface ReviewReport {
  id: string;
  type: ReviewReportType;
  reviewer: string;
  reviewerName: string;
  backlogItemId: string | null;
  sprintId: string | null;
  project: string;
  subject: string;
  rating: number | null;
  qaStatus: QAStatus | null;
  findings: ReviewFinding[];
  summary: string;
  fullReport: string;
  filesReviewed: string[];
  createdAt: string;
  metadata: Record<string, unknown>;
}

/** Review report statistics from /api/review-reports/stats */
export interface ReviewReportStats {
  totalReports: number;
  byReviewer: Record<string, number>;
  byType: Record<string, number>;
  ratingCounts: Record<string, number>;
  qaCounts: Record<string, number>;
  latestRating: number | null;
  latestQaStatus: string | null;
}

// ── Review Reports API ──────────────────────────────────────────────

const REVIEW_REPORTS_PATH = '/review-reports';

/** List review reports with optional filters */
export async function getReviewReports(params?: {
  type?: ReviewReportType;
  reviewer?: string;
  project?: string;
  backlogItemId?: string;
}): Promise<ReviewReport[]> {
  const response = await apiClient.get<ReviewReport[]>(REVIEW_REPORTS_PATH, { params });
  return response.data;
}

/** Get a single review report by ID */
export async function getReviewReport(id: string): Promise<ReviewReport> {
  const response = await apiClient.get<ReviewReport>(`${REVIEW_REPORTS_PATH}/${id}`);
  return response.data;
}

/** Create a new review report */
export async function createReviewReport(data: {
  type: ReviewReportType;
  reviewer: string;
  reviewerName: string;
  backlogItemId?: string;
  sprintId?: string;
  project: string;
  subject: string;
  rating?: number;
  qaStatus?: QAStatus;
  findings?: ReviewFinding[];
  summary: string;
  fullReport: string;
  filesReviewed?: string[];
  metadata?: Record<string, unknown>;
}): Promise<ReviewReport> {
  const response = await apiClient.post<ReviewReport>(REVIEW_REPORTS_PATH, data);
  return response.data;
}

/** Get review report summary statistics */
export async function getReviewReportStats(): Promise<ReviewReportStats> {
  const response = await apiClient.get<ReviewReportStats>(`${REVIEW_REPORTS_PATH}/stats`);
  return response.data;
}

/** Record a review result on a backlog item */
export async function recordBacklogReview(
  id: string,
  data: {
    reviewer: string;
    rating?: number;
    status?: 'pass' | 'needs_fixes';
    comment?: string;
  },
): Promise<BacklogItem> {
  const response = await apiClient.put<BacklogItem>(
    `${BACKLOG_PATH}/${id}/review`,
    data,
  );
  return response.data;
}

// ── Recurring Task Types ──────────────────────────────────────────

export type RecurringTaskScheduleType = 'once' | 'daily' | 'weekly' | 'monthly';
export type RecurringTaskStatus = 'draft' | 'pending_approval' | 'active' | 'paused' | 'completed' | 'archived';
export type ExecutionStatus = 'pending' | 'running' | 'success' | 'failure' | 'skipped' | 'budget_exceeded';

export interface RecurringTask {
  id: string;
  title: string;
  description: string;
  scheduleType: RecurringTaskScheduleType;
  scheduledAt?: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay: string;
  timezone: string;
  assignedAgent: string;
  agentPrompt: string;
  projectId: string | null;
  tokenBudgetPerRun: number;
  createdBy: string;
  status: RecurringTaskStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  alertOnSuccess: boolean;
  alertOnFailure: boolean;
  alertChannels: { inApp: boolean; email: boolean };
  alertEmails: string[];
  lastRunAt: string | null;
  nextRunAt: string | null;
  totalRuns: number;
  totalTokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

export interface RecurringTaskExecution {
  id: string;
  recurringTaskId: string;
  status: ExecutionStatus;
  triggeredAt: string;
  startedAt: string | null;
  completedAt: string | null;
  agentId: string;
  tokensUsed: number;
  result: string | null;
  error: string | null;
  backlogItemId: string | null;
  createdAt: string;
}

export interface RecurringTaskConfig {
  monthlyTokenBudget: number;
  tokensUsedThisMonth: number;
  budgetResetDay: number;
  lastResetMonth: number;
  emailEnabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  fromEmail: string;
  fromName: string;
  delegates: string[];
  enabled: boolean;
  checkIntervalSeconds: number;
}

// ── Recurring Task API ────────────────────────────────────────────

const RECURRING_TASKS_PATH = '/recurring-tasks';

export async function getRecurringTasks(status?: string, agentId?: string): Promise<RecurringTask[]> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (agentId) params.agentId = agentId;
  const response = await apiClient.get<RecurringTask[]>(RECURRING_TASKS_PATH, { params });
  return response.data;
}

export async function getRecurringTask(id: string): Promise<RecurringTask> {
  const response = await apiClient.get<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}`);
  return response.data;
}

export async function createRecurringTask(data: {
  title: string;
  description?: string;
  scheduleType: RecurringTaskScheduleType;
  scheduledAt?: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
  timezone?: string;
  assignedAgent: string;
  agentPrompt: string;
  projectId?: string;
  tokenBudgetPerRun?: number;
  alertOnSuccess?: boolean;
  alertOnFailure?: boolean;
  alertChannels?: { inApp: boolean; email: boolean };
  alertEmails?: string[];
}): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(RECURRING_TASKS_PATH, data);
  return response.data;
}

export async function updateRecurringTask(id: string, data: Partial<RecurringTask>): Promise<RecurringTask> {
  const response = await apiClient.put<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}`, data);
  return response.data;
}

export async function deleteRecurringTask(id: string): Promise<{ ok: boolean }> {
  const response = await apiClient.delete<{ ok: boolean }>(`${RECURRING_TASKS_PATH}/${id}`);
  return response.data;
}

export async function submitRecurringTask(id: string): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}/submit`);
  return response.data;
}

export async function approveRecurringTask(id: string): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}/approve`);
  return response.data;
}

export async function rejectRecurringTask(id: string, reason: string): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}/reject`, { reason });
  return response.data;
}

export async function pauseRecurringTask(id: string): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}/pause`);
  return response.data;
}

export async function resumeRecurringTask(id: string): Promise<RecurringTask> {
  const response = await apiClient.post<RecurringTask>(`${RECURRING_TASKS_PATH}/${id}/resume`);
  return response.data;
}

export async function runRecurringTaskNow(id: string): Promise<RecurringTaskExecution> {
  const response = await apiClient.post<RecurringTaskExecution>(`${RECURRING_TASKS_PATH}/${id}/run-now`);
  return response.data;
}

export async function getRecurringTaskExecutions(taskId: string, limit?: number): Promise<RecurringTaskExecution[]> {
  const params: Record<string, string> = {};
  if (limit) params.limit = String(limit);
  const response = await apiClient.get<RecurringTaskExecution[]>(`${RECURRING_TASKS_PATH}/${taskId}/executions`, { params });
  return response.data;
}

export async function getAllRecurringExecutions(limit?: number): Promise<RecurringTaskExecution[]> {
  const params: Record<string, string> = {};
  if (limit) params.limit = String(limit);
  const response = await apiClient.get<RecurringTaskExecution[]>(`${RECURRING_TASKS_PATH}/executions`, { params });
  return response.data;
}

export async function getRecurringTaskConfig(): Promise<RecurringTaskConfig> {
  const response = await apiClient.get<RecurringTaskConfig>(`${RECURRING_TASKS_PATH}/config`);
  return response.data;
}

export async function updateRecurringTaskConfig(config: Partial<RecurringTaskConfig>): Promise<RecurringTaskConfig> {
  const response = await apiClient.put<RecurringTaskConfig>(`${RECURRING_TASKS_PATH}/config`, config);
  return response.data;
}
